import { createElement, forwardRef } from 'react';

import { useTheme } from '@mui/material/styles';
import {
  borders,
  display,
  flexbox,
  grid,
  positions,
  shadows,
  sizing,
  typography,
} from '@mui/system';
import styled from 'styled-components';
import get from 'lodash/get';

import { Spacing, BorderRadius } from '../Theme';

import {
  BoxProps,
  StyledBoxProps,
  SpacingValues,
  RadiusSize,
} from './Box.types';

/**
 * Возвращает строку радиуса с использованием значений из темы.
 *
 * @param radius - Размер радиуса, который может быть строкой или массивом строк.
 * @param radiusTokens - Объект с токенами радиусов из темы.
 * @returns Строка с радиусом или undefined, если radius или radiusTokens не заданы.
 */
const getRadiusString = (radius?: RadiusSize, radiusTokens?: BorderRadius) => {
  if (!radius || !radiusTokens) {
    return undefined;
  }

  return Array.isArray(radius)
    ? radius.map((el) => `${radiusTokens[el]}px`).join(' ')
    : `${radiusTokens[radius]}`.concat(radius === 'round' ? '' : 'px'); // Если radius = 50%, то не добавляем в конец px, чтобы не сломать стили (иначе получится radius: 50%px).
};

/**
 * Настраиваемый компонент-Box, созданный с использованием Material-UI и styled-components.
 *
 * @public
 * @param children - Содержимое, отображаемое внутри Box.
 * @param className - Имя класса для Box.
 * @param style - Инлайн-стили для Box.
 * @param id - Идентификатор для Box.
 * @param tabIndex - Индекс табуляции для Box.
 * @param cursor - Тип курсора для Box.
 * @param onClick - Обработчик события клика.
 * @param onFocus - Обработчик события фокуса.
 * @param onBlur - Обработчик события потери фокуса.
 * @param onChange - Обработчик события изменения.
 * @param onContextMenu - Обработчик события контекстного меню.
 * @param onDrag - Обработчик события начала перетаскивания.
 * @param onDragEnd - Обработчик события окончания перетаскивания.
 * @param onDragEnter - Обработчик события входа перетаскиваемого элемента.
 * @param onDragExit - Обработчик события выхода перетаскиваемого элемента.
 * @param onDragLeave - Обработчик события покидания перетаскиваемым элементом.
 * @param onDragOver - Обработчик события прохождения над перетаскиваемым элементом.
 * @param onDragStart - Обработчик события начала перетаскивания.
 * @param onDrop - Обработчик события отпускания перетаскиваемого элемента.
 * @param onKeyDown - Обработчик события нажатия клавиши.
 * @param onKeyPress - Обработчик события удержания клавиши.
 * @param onKeyUp - Обработчик события отпускания клавиши.
 * @param onMouseDown - Обработчик события нажатия мыши.
 * @param onMouseEnter - Обработчик события входа мыши.
 * @param onMouseLeave - Обработчик события выхода мыши.
 * @param onMouseMove - Обработчик события перемещения мыши.
 * @param onMouseOut - Обработчик события покидания мыши.
 * @param onMouseOver - Обработчик события наведения мыши.
 * @param onMouseUp - Обработчик события отпускания мыши.
 * @param testId - Тестовый идентификатор для Box.
 * @param component - Компонент, который будет использоваться в качестве корневого элемента.
 * @param gap - Расстояние между элементами сетки.
 * @param transform - Свойство transform.
 * @param m - Значение отступа.
 * @param mt - Значение отступа сверху.
 * @param mr - Значение отступа справа.
 * @param mb - Значение отступа снизу.
 * @param ml - Значение отступа слева.
 * @param mx - Значение отступа слева и справа.
 * @param my - Значение отступа сверху и снизу.
 * @param p - Значение заполнения.
 * @param pt - Значение заполнения сверху.
 * @param pr - Значение заполнения справа.
 * @param pb - Значение заполнения снизу.
 * @param pl - Значение заполнения слева.
 * @param px - Значение заполнения слева и справа.
 * @param py - Значение заполнения сверху и снизу.
 * @param margin - Значение отступа.
 * @param marginTop - Значение отступа сверху.
 * @param marginRight - Значение отступа справа.
 * @param marginBottom - Значение отступа снизу.
 * @param marginLeft - Значение отступа слева.
 * @param marginX - Значение отступа слева и справа.
 * @param marginY - Значение отступа сверху и снизу.
 * @param padding - Значение заполнения.
 * @param paddingTop - Значение заполнения сверху.
 * @param paddingRight - Значение заполнения справа.
 * @param paddingBottom - Значение заполнения снизу.
 * @param paddingLeft - Значение заполнения слева.
 * @param paddingX - Значение заполнения слева и справа.
 * @param paddingY - Значение заполнения сверху и снизу.
 * @param bgcolor - Цвет фона.
 * @param color - Цвет текста.
 * @param borderColor - Цвет границы.
 * @param radius - Радиус границы.
 * @returns Компонент Box.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link BoxProps}
 * Ссылка на свойства для `styled-components` {@link StyledBoxProps}
 */
export const Box = styled(
  forwardRef(({ component, testId = 'box', ...props }: BoxProps, ref) => {
    const {
      children,
      className,
      style,
      id,
      tabIndex,
      cursor,
      onClick,
      onFocus,
      onBlur,
      onChange,
      onContextMenu,
      onDrag,
      onDragEnd,
      onDragEnter,
      onDragExit,
      onDragLeave,
      onDragOver,
      onDragStart,
      onDrop,
      onKeyDown,
      onKeyPress,
      onKeyUp,
      onMouseDown,
      onMouseEnter,
      onMouseLeave,
      onMouseMove,
      onMouseOut,
      onMouseOver,
      onMouseUp,
      onTouchStart,
      onTouchEnd,
      title,
      'data-mui-internal-clone-element': dataMuiInternalCloneElement,
    } = props;

    const htmlProps = {
      ref,
      children,
      className: `aqa_box${className ? ` ${className}` : ''}`,
      style,
      id,
      tabIndex,
      cursor,
      onClick,
      onFocus,
      onBlur,
      onChange,
      onContextMenu,
      onDrag,
      onDragEnd,
      onDragEnter,
      onDragExit,
      onDragLeave,
      onDragOver,
      onDragStart,
      onDrop,
      onKeyDown,
      onKeyPress,
      onKeyUp,
      onMouseDown,
      onMouseEnter,
      onMouseLeave,
      onMouseMove,
      onMouseOut,
      onMouseOver,
      onMouseUp,
      onTouchStart,
      onTouchEnd,
      title,
      'data-mui-internal-clone-element': dataMuiInternalCloneElement,
      'data-testid': testId,
    };

    if (!component) {
      return createElement('div', htmlProps);
    } else if (typeof component === 'string') {
      return createElement(component, htmlProps);
    } else {
      const Component = component as React.ElementType;

      return (
        <Component
          ref={ref}
          {...props}
          className={`aqa_box${className ? ` ${className}` : ''}`}
        />
      );
    }
  }),
)<StyledBoxProps>(({
  gap,
  transform,
  m,
  mt,
  mr,
  mb,
  ml,
  mx,
  my,
  p,
  pt,
  pr,
  pb,
  pl,
  px,
  py,
  margin,
  marginTop,
  marginRight,
  marginBottom,
  marginLeft,
  marginX,
  marginY,
  padding,
  paddingTop,
  paddingRight,
  paddingBottom,
  paddingLeft,
  paddingX,
  paddingY,
  bgcolor,
  color,
  borderColor,
  radius,
  cursor,
  pointerEvents,
  borderBottomRightRadius,
  borderTopRightRadius,
  borderBottomLeftRadius,
  borderTopLeftRadius,
  clipPath,
  scrollbarWidth,
  opacity,
  transition,
  wordBreak,
  overflowWrap,
  maskImage,
  WebkitMaskImage,
  ...props
}: StyledBoxProps) => {
  const theme = useTheme();

  const styles = {
    ...borders({
      ...props,
      borderRadius: getRadiusString(radius, theme.tokens.radius),
      borderColor: undefined,
    }),
    borderBottomRightRadius: getRadiusString(
      borderBottomRightRadius,
      theme.tokens.radius,
    ),
    borderTopRightRadius: getRadiusString(
      borderTopRightRadius,
      theme.tokens.radius,
    ),
    borderBottomLeftRadius: getRadiusString(
      borderBottomLeftRadius,
      theme.tokens.radius,
    ),
    borderTopLeftRadius: getRadiusString(
      borderTopLeftRadius,
      theme.tokens.radius,
    ),
    ...display(props),
    ...flexbox(props),
    ...grid(props),
    ...positions(props),
    ...shadows(props),
    ...sizing(props),
    ...typography(props),
    cursor,
    clipPath,
    pointerEvents: pointerEvents || 'auto',
    opacity: opacity || 1.0,
    transition,
    scrollbarWidth,
    wordBreak,
    overflowWrap,
    maskImage,
    WebkitMaskImage,
    gap:
      gap &&
      theme.tokens.spacing[
        Math.abs(parseInt(gap, 10)).toString() as keyof Spacing
      ],
    transform,
    ...(bgcolor && {
      backgroundColor: get(theme.tokens.colors, bgcolor),
    }),
    ...(color && {
      color: get(theme.tokens.colors, color),
    }),
    ...(borderColor && {
      borderColor: get(theme.tokens.colors, borderColor),
    }),

    createMediaQuery(
      breakpoint: 'xs' | 'sm' | 'md' | 'lg' | 'xl',
      propertyName: string,
      value: SpacingValues | undefined,
    ) {
      if (typeof value === 'undefined' || value === null) return;

      this[theme.breakpoints.up(breakpoint)] = {
        ...this[theme.breakpoints.up(breakpoint)],
        [propertyName]:
          value === 'auto' || value === 'unset'
            ? value
            : (value.startsWith('-') ? -1 : 1) *
              theme.tokens.spacing[
                Math.abs(parseInt(value, 10)).toString() as keyof Spacing
              ],
      };
    },
    createSpacingStyles(
      propertyName: string,
      value:
        | SpacingValues
        | {
            xs?: SpacingValues;
            sm?: SpacingValues;
            md?: SpacingValues;
            lg?: SpacingValues;
            xl?: SpacingValues;
          },
    ) {
      if (typeof value === 'undefined' || value === null) return;

      if (typeof value === 'string') {
        this[propertyName] =
          value === 'auto' || value === 'unset'
            ? value
            : (value.startsWith('-') ? -1 : 1) *
              theme.tokens.spacing[
                Math.abs(parseInt(value, 10)).toString() as keyof Spacing
              ];
      } else {
        if (!Object.keys(value).length || Array.isArray(value)) return;

        const { xs, sm, md, lg, xl } = value;
        this.createMediaQuery('xs', propertyName, xs);
        this.createMediaQuery('sm', propertyName, sm);
        this.createMediaQuery('md', propertyName, md);
        this.createMediaQuery('lg', propertyName, lg);
        this.createMediaQuery('xl', propertyName, xl);
      }
    },
  };

  styles.createSpacingStyles('margin', margin || m);
  styles.createSpacingStyles('marginTop', marginTop || mt);
  styles.createSpacingStyles('marginRight', marginRight || mr);
  styles.createSpacingStyles('marginBottom', marginBottom || mb);
  styles.createSpacingStyles('marginLeft', marginLeft || ml);
  styles.createSpacingStyles('marginLeft', marginX || mx);
  styles.createSpacingStyles('marginRight', marginX || mx);
  styles.createSpacingStyles('marginTop', marginY || my);
  styles.createSpacingStyles('marginBottom', marginY || my);
  styles.createSpacingStyles('padding', padding || p);
  styles.createSpacingStyles('paddingTop', paddingTop || pt);
  styles.createSpacingStyles('paddingRight', paddingRight || pr);
  styles.createSpacingStyles('paddingBottom', paddingBottom || pb);
  styles.createSpacingStyles('paddingLeft', paddingLeft || pl);
  styles.createSpacingStyles('paddingLeft', paddingX || px);
  styles.createSpacingStyles('paddingRight', paddingX || px);
  styles.createSpacingStyles('paddingTop', paddingY || py);
  styles.createSpacingStyles('paddingBottom', paddingY || py);

  return styles;
});

Box.displayName = 'Box';
